import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, type BomPayload, type BomResult } from '../api/client';

// Internal BOM (§5.9). Full internal detail — material cost, labor hours, source,
// confidence. INTERNAL ONLY: proposals never show this. Generated by cascading
// human counts through device assemblies. Pricing/markup land in Phase 6.
const money = (n: number | null | undefined) => n == null ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const hrs = (n: number | null | undefined) => n == null ? '—' : n.toFixed(2);

export function Bom() {
  const { id = '' } = useParams();
  const [data, setData] = useState<BomPayload | null>(null);
  const [result, setResult] = useState<BomResult | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    try { setData(await api.getBom(id)); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to load BOM'); }
  }
  useEffect(() => { void load(); }, [id]);

  async function generate() {
    setBusy(true); setError(''); setResult(null);
    try {
      const res = await api.generateBom(id);
      setResult(res.result);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : 'BOM generation failed'); }
    finally { setBusy(false); }
  }

  const lines = data?.lines ?? [];
  const totals = data?.totals;

  return (
    <div>
      <Link to={`/projects/${id}`} className="muted" style={{ fontSize: 13 }}>← Scope Analysis</Link>
      <h1 style={{ margin: '4px 0 0' }}>Internal BOM</h1>
      <div className="banner" style={{ marginTop: 8 }}>
        <strong>Internal only.</strong> Full material, labor and source detail. This is never shown to customers —
        proposals carry the final number alone. Quantities came from your count sheet; the software never counts.
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0 }}>Generate from counts</h2>
            <p className="muted" style={{ margin: '4px 0 0' }}>
              Cascades each counted symbol → confirmed device → assembly parts. Material is internet-estimated
              until distributor pricing (Phase 6); labor is NECA MLU / BICSI hours.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={generate} disabled={busy}>
              {busy ? 'Generating…' : 'Generate BOM'}
            </button>
            <Link className="btn btn-ghost" to={`/projects/${id}/costing`}>Pricing & labor →</Link>
          </div>
        </div>
        {error && <div className="error" style={{ marginTop: 10 }}>{error}</div>}
        {result && (
          <div className="banner" style={{ marginTop: 12 }}>
            <strong>{result.lines} line items.</strong> Material {money(result.total_material)} · Labor {hrs(result.total_labor_hours)} hrs.
            {result.uncounted.length > 0 && <div style={{ color: 'var(--warning)' }}>Skipped (no count): {result.uncounted.join(', ')}</div>}
            {result.no_assembly.length > 0 && <div style={{ color: 'var(--warning)' }}>No catalog assembly: {result.no_assembly.join(', ')}</div>}
          </div>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h2>Bill of materials</h2>
          {totals && totals.line_count > 0 && (
            <div className="muted">{totals.line_count} lines · material <strong>{money(totals.material)}</strong> · labor <strong>{hrs(totals.labor_hours)} hrs</strong></div>
          )}
        </div>
        {!lines.length && <p className="muted">No BOM yet. Resolve mappings, import counts, then Generate BOM.</p>}
        {lines.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>System</th><th>Part #</th><th>Mfr</th><th>Description</th><th>Qty</th><th>Unit</th>
                  <th>Unit mat.</th><th>Ext. mat.</th><th>Labor u/hrs</th><th>Ext. hrs</th>
                  <th>Labor cat.</th><th>Source</th><th>Conf.</th><th>Ref</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.id}>
                    <td className="muted">{l.system ?? '—'}</td>
                    <td><strong>{l.part_number}</strong></td>
                    <td>{l.manufacturer}</td>
                    <td>{l.description}</td>
                    <td>{l.qty}</td>
                    <td className="muted">{l.unit}</td>
                    <td>{money(l.unit_material_cost)}</td>
                    <td>{money(l.extended_material_cost)}</td>
                    <td>{hrs(l.labor_unit_hours)}</td>
                    <td>{hrs(l.extended_labor_hours)}</td>
                    <td className="muted">{l.labor_category}</td>
                    <td><span className="pill pill-proposed">{l.price_source}</span></td>
                    <td>{l.confidence_score != null ? `${Math.round(l.confidence_score * 100)}%` : '—'}</td>
                    <td className="muted" style={{ fontSize: 12 }}>{l.source_reference}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
